import { Box, Paper } from '@mui/material';
import Button from '@mui/material/Button';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';

import { listSorter } from '@/assets/js/util';
import BasicModal from '@/components/common/BasicModal';
import AcknowledgementModalTable from '@/components/Patient/components/AcknowledgementModal/AcknowledgementModalTable';
import { usePatientPrescriptions } from '@/hooks/patients/usePatientPrescriptions';
import { useState } from 'react';

export type MedicationData = [
  string, // Script created
  string, // Product name
  number, // Quantity
  number, // Interval
  number, // Repeats
];

type AcknowledgementModalProps = {
  doctorName: string;
  doctorStatus: boolean;
  isDoc: boolean;
  patientId: number;
};

type PrescribedProductItem = {
  Product?: {
    formulation_id: number;
    name?: string;
  } | null;
  other_product?: string;
  created_date: string;
  quantity?: number;
  interval?: number;
  repeats?: number;
};

type Prescription = {
  PrescribedProducts: PrescribedProductItem[];
};

const StyledBasicModal = styled(BasicModal)({
  outline: 'none',
  position: 'absolute' as const,
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '50%',
  '& iframe': {
    border: 0,
    width: '100%',
    height: '100%',
  },
});

/**
 * AcknowledgementModalV3 component
 *
 * @remarks
 * Renders a modal with a warning message (for newly assigned doctors) and a table of all the ongoing prescriptions which the patient has.
 *
 */

export const AcknowledgementModal = (props: AcknowledgementModalProps) => {
  const { doctorName, doctorStatus, patientId, isDoc } = props;
  const { patientPrescriptionsData } = usePatientPrescriptions(patientId);
  const [hasAcknowledged, setHasAcknowledged] = useState(false);

  if (!isDoc || !doctorStatus) {
    return null;
  }

  const prescriptionData = patientPrescriptionsData ? listSorter(patientPrescriptionsData, 'order_date') : [];

  const medicationData: MedicationData[] = prescriptionData.flatMap((prescription: Prescription) => {
    return prescription.PrescribedProducts.filter(
      (item: PrescribedProductItem) => item.Product && item.Product.formulation_id !== 5,
    ).map((item) => [
      item.created_date,
      (item.Product?.name || item.other_product) as string,
      item.quantity as number,
      item.interval as number,
      item.repeats as number,
    ]);
  });

  const shouldShowModal = !hasAcknowledged && medicationData.length > 0;

  const handleAcknowledgementModalClose = () => {
    setHasAcknowledged(true);
  };

  return (
    <StyledBasicModal open={shouldShowModal} handleClose={handleAcknowledgementModalClose}>
      <Paper>
        <Box p={10} display={'flex'} flexDirection={'column'} justifyContent={'center'}>
          <Typography align="center" mb={10} variant="body1">
            This patient was previously prescribed under Dr. {doctorName}. Please re-prescribe as appropriate, the
            patient’s older prescriptions will be revoked and no longer accessible after this consultation. You can also
            find this information in the Patient notes section.
          </Typography>
          <AcknowledgementModalTable medicationData={medicationData} />
          <Box mt={10} textAlign={'center'}>
            <Button variant="contained" color="secondary" size="large" onClick={handleAcknowledgementModalClose}>
              I Understand
            </Button>
          </Box>
        </Box>
      </Paper>
    </StyledBasicModal>
  );
};
